import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";

export default function DiscordLoginModal({ open, onClose }) {
  const { setSession } = useAuth();
  const [username, setUsername] = useState("");
  const [fivemId, setFivemId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [discordConfigured, setDiscordConfigured] = useState(false);
  const [discordUrl, setDiscordUrl] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError("");
    api.discordUrl().then((r) => {
      setDiscordConfigured(r.configured);
      setDiscordUrl(r.url);
    }).catch(() => {});
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim()) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.discordDemo({
        discord_username: username.trim(),
        fivem_id: fivemId.trim() || null,
      });
      setSession(res);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={onClose} />
          <motion.div
            data-testid="login-modal"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.19, 1, 0.22, 1] }}
            className="relative w-full max-w-md bg-[color:var(--bg-surface)] border border-white/10 mx-4"
          >
            <button
              onClick={onClose}
              data-testid="login-modal-close"
              className="absolute top-4 right-4 text-zinc-400 hover:text-white"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="uj-bar" />

            <div className="p-8">
              <div className="eyebrow mb-3">/ CLEARANCE REQUIRED</div>
              <h2 className="font-display text-3xl leading-none text-white mb-2">
                Log in.<br />
                <span className="text-[color:var(--accent-red)]">Claim your ends.</span>
              </h2>
              <p className="text-sm text-zinc-400 mt-3 mb-6">
                Sign in with Discord to purchase, link your FiveM ID and track deliveries.
              </p>

              {discordConfigured && discordUrl && (
                <a
                  href={discordUrl}
                  data-testid="discord-oauth-cta"
                  className="btn-brutal block text-center w-full h-11 border border-white/15 font-mono text-[11px] tracking-[0.24em] uppercase leading-[44px] mb-6"
                >
                  Continue with Discord
                </a>
              )}

              {!discordConfigured && (
                <div className="mb-6">
                  <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">
                    // Demo mode — enter your Discord handle
                  </div>
                </div>
              )}

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="eyebrow block mb-2">DISCORD USERNAME</label>
                  <input
                    data-testid="login-username-input"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="terminal-input"
                    placeholder="yourhandle"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2">FIVEM ID (OPTIONAL)</label>
                  <input
                    data-testid="login-fivemid-input"
                    value={fivemId}
                    onChange={(e) => setFivemId(e.target.value)}
                    className="terminal-input"
                    placeholder="steam:110000... or in-game name"
                  />
                </div>

                {error && (
                  <div className="font-mono text-[11px] text-[color:var(--accent-red)]">{error}</div>
                )}

                <button
                  type="submit"
                  disabled={loading || !username.trim()}
                  data-testid="login-submit-btn"
                  className="btn-brutal w-full h-11 border border-white/15 text-white font-mono text-[11px] tracking-[0.24em] uppercase disabled:opacity-50"
                >
                  {loading ? "Authenticating..." : "Enter The Ends"}
                </button>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
